import { useContext, useState } from "react";
import styles from "./OnboardingOrganisation.module.scss";
import { useTranslation } from "../../i18n";
import { Button, CircularProgress, TextField } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import classNames from "classnames";
import { fetcher, isFetcherError } from "../../utils/fetcher";
import { AuthContext } from "../../context/AuthContext";
import { InputAddress } from "../InputAddress/InputAddress";

interface OnboardingOrganisationFirstStepProps {
  title: string;
  desc: string;
  onSetError: (error: string | false) => void;
  onDuplicateEmail: () => void;
}

interface FormData {
  name: string;
  ic_dic: number;
  hq_address: string;
  website_link: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
}

export const OnboardingOrganisationFirstStep = ({
  title,
  desc,
  onSetError,
  onDuplicateEmail,
}: OnboardingOrganisationFirstStepProps) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    ic_dic: null as any,
    hq_address: "",
    website_link: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const isValid =
    formData.name.trim() !== "" &&
    formData.ic_dic !== null &&
    formData.hq_address.trim() !== "";

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await fetcher({
        url: "/organisation",
        method: "POST",
        body: JSON.stringify(formData),
      });
      authContext?.refreshUser();
    } catch (error) {
      if (isFetcherError(error) && error.status === 409) {
        onDuplicateEmail();
        return;
      }
      console.error("Error creating organisation:", error);
      onSetError("onboarding.alerts.orgFailed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classNames(styles.content, styles.small)}>
      <h2>{title}</h2>
      <p>{desc}</p>
      <form className={styles.form}>
        <TextField
          label={t("orgProfile.name")}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="number"
          label={t("orgProfile.registrationId")}
          value={formData.ic_dic}
          onChange={(e) =>
            setFormData({
              ...formData,
              ic_dic: e.target.value ? Number(e.target.value) : (null as any),
            })
          }
          required
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <InputAddress
          editMode={true}
          address={{ address_text: formData.hq_address }}
          onAddressChange={(value) =>
            setFormData((prevData) => ({
              ...prevData,
              hq_address: value || "",
            }))
          }
          required
        />
        <TextField
          label={t("orgProfile.website")}
          value={formData.website_link}
          onChange={(e) =>
            setFormData({ ...formData, website_link: e.target.value })
          }
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label={t("orgProfile.coordinator")}
          value={formData.contact_person}
          onChange={(e) =>
            setFormData({ ...formData, contact_person: e.target.value })
          }
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="tel"
          label={t("orgProfile.coordinatorPhone")}
          value={formData.contact_phone}
          onChange={(e) =>
            setFormData({ ...formData, contact_phone: e.target.value })
          }
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="email"
          label={t("orgProfile.coordinatorEmail")}
          value={formData.contact_email}
          onChange={(e) =>
            setFormData({ ...formData, contact_email: e.target.value })
          }
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button
          variant="contained"
          size="medium"
          endIcon={
            submitting ? <CircularProgress size={20} /> : <NavigateNextIcon />
          }
          onClick={handleCreate}
          disabled={!isValid || submitting}
        >
          {t("onboarding.continue")}
        </Button>
      </form>
    </div>
  );
};
